import { useMutation } from '@apollo/client';
import { useState, useCallback } from 'react';
import { submitInspectionResponseMutation } from 'graphql-queries';

/**
 * Input for submitting an inspection response
 */
export interface SubmitInspectionResponseInput {
  channelId: string;
  instanceId: string;
  itemId: string;
  value: unknown;
  notes?: string;
  additionalFields?: Record<string, unknown>;
  isDeficient: boolean;
  respondedAt: Date;
}

/**
 * GraphQL mutation result type
 */
type SubmitInspectionResponseMutationResult = {
  submitInspectionResponse: {
    success: boolean;
    newVersion: number;
  } | null;
};

/**
 * GraphQL mutation variables type
 */
type SubmitInspectionResponseMutationVariables = {
  input: {
    channelId: string;
    instanceId: string;
    itemId: string;
    value: unknown;
    notes?: string;
    additionalFields?: Record<string, any>;
    isDeficient: boolean;
    respondedAt: Date;
  };
};

/**
 * Result from useSubmitInspectionResponse hook
 */
export interface UseSubmitInspectionResponseResult {
  submitResponse: (
    input: SubmitInspectionResponseInput
  ) => Promise<{ success: boolean; newVersion: number }>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Hook to submit an inspection item response to the server.
 * Wraps the GraphQL mutation with loading and error state management.
 *
 * @returns Hook result with submitResponse function, loading state, and error
 *
 * @example
 * ```tsx
 * const { submitResponse, loading, error } = useSubmitInspectionResponse();
 *
 * try {
 *   const result = await submitResponse({
 *     channelId: '123',
 *     instanceId: '456',
 *     itemId: '789',
 *     value: 'pass',
 *     isDeficient: false,
 *     respondedAt: new Date(),
 *   });
 *   console.log('Success:', result.newVersion);
 * } catch (err) {
 *   console.error('Failed to submit response:', err);
 * }
 * ```
 */
export function useSubmitInspectionResponse(): UseSubmitInspectionResponseResult {
  const [mutate] = useMutation<
    SubmitInspectionResponseMutationResult,
    SubmitInspectionResponseMutationVariables
  >(submitInspectionResponseMutation);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const submitResponse = useCallback(
    async (input: SubmitInspectionResponseInput) => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await mutate({
          variables: {
            input: {
              channelId: input.channelId,
              instanceId: input.instanceId,
              itemId: input.itemId,
              value: input.value,
              notes: input.notes,
              additionalFields: input.additionalFields,
              isDeficient: input.isDeficient,
              respondedAt: input.respondedAt,
            },
          },
        });

        if (!result.data?.submitInspectionResponse) {
          throw new Error('Failed to submit inspection response');
        }

        return {
          success: result.data.submitInspectionResponse.success,
          newVersion: result.data.submitInspectionResponse.newVersion,
        };
      } catch (err) {
        const wrappedError =
          err instanceof Error ? err : new Error('Unknown error occurred');

        setError(wrappedError);

        throw wrappedError;
      } finally {
        setLoading(false);
      }
    },
    [mutate]
  );

  return {
    submitResponse,
    loading,
    error,
  };
}
